import { getSupabaseClient } from "../supabase/supabase";
import { dashboardRequestError } from "./client";

/**
 * The test-echo autoqueue does NOT live behind the dashboard API — it is the
 * WhatsApp bot's own internal API, reached through its own Vercel proxy
 * (src/server/testEchoProxy.ts). It therefore gets its own tiny request helper
 * rather than bending `awsDashboardRequest`, whose base URL is operator
 * configurable (VITE_ECHOFLICKS_DASHBOARD_API_URL can point straight at the
 * dashboard API, bypassing the proxy) and would send the wrong path entirely.
 *
 * Errors are surfaced as the same DashboardRequestError the rest of the CRM
 * already renders, so `status` (403/404/409) and `code` ("forbidden", "busy")
 * and `payload.reason` reach the Labs card intact.
 */
const TEST_ECHO_PROXY_PATH = "/api/test-echoes";

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function testEchoRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const response = await fetch(`${TEST_ECHO_PROXY_PATH}${normalizedPath}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
      ...(await authHeaders()),
    },
  });

  const body = await response.text();
  const parsed = parseJsonBody(body);

  if (!response.ok) {
    throw dashboardRequestError(response.status, parsed, body);
  }

  return parsed as T;
}

function parseJsonBody(body: string) {
  if (!body) return null;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}
